// RoomPanel component for displaying rooms and their gateways

import React from 'react';
import Icon from '../../../core/Icon';
import { TEST_BEACONS, ROOM_TYPE_ICONS, ROOM_TYPE_ORDER, ENROLLMENT_READER_GATEWAY_ID } from '../constants';
import type { TestBeaconExtended } from '../constants';
import type { DynamicBeacon } from '../hooks/useSimulation';
import { generateGatewayId } from '../utils';
import {
  Panel,
  PanelTitle,
  RoomTypeSection,
  RoomTypeHeader,
  RoomGrid,
  RoomCardContainer,
  RoomName,
  GatewayInfo,
  BeaconsInRoom,
  BeaconDot,
} from '../styles';
import type { Room, RoomsByType, ExistingTestGateways, BeaconLocations, TestBeacon } from '../types';

interface RoomPanelProps {
  roomsByType: RoomsByType;
  selectedBeacon: string | null;
  existingTestGateways: ExistingTestGateways;
  beaconLocations: BeaconLocations;
  dynamicBeacons: DynamicBeacon[];
  onRoomClick: (room: Room) => void;
  onEnrollmentReaderClick: () => void;
}

interface RoomTypeSectionComponentProps {
  roomType: string;
  rooms: Room[];
  selectedBeacon: string | null;
  existingTestGateways: ExistingTestGateways;
  beaconLocations: BeaconLocations;
  onRoomClick: (room: Room) => void;
}

const getBeaconsInRoom = (room: Room, beaconLocations: BeaconLocations): TestBeacon[] => {
  const gatewayId = generateGatewayId(room);
  return TEST_BEACONS.filter(b => beaconLocations[b.id] === gatewayId);
};

const RoomTypeSectionComponent: React.FC<RoomTypeSectionComponentProps> = ({
  roomType,
  rooms,
  selectedBeacon,
  existingTestGateways,
  beaconLocations,
  onRoomClick,
}) => {
  if (rooms.length === 0) return null;

  return (
    <RoomTypeSection>
      <RoomTypeHeader>
        <Icon className="material-icons" style={{ fontSize: '1.2rem' }}>
          {ROOM_TYPE_ICONS[roomType] || 'room'}
        </Icon>
        {roomType} ({rooms.length})
      </RoomTypeHeader>
      <RoomGrid>
        {rooms.map(room => {
          const gwId = generateGatewayId(room);
          const hasGateway = !!existingTestGateways[gwId];
          const beaconsInRoom = getBeaconsInRoom(room, beaconLocations);

          return (
            <RoomCardContainer
              key={room.id}
              hasGateway={hasGateway}
              clickable={!!selectedBeacon}
              onClick={() => selectedBeacon && onRoomClick(room)}
            >
              <RoomName>{room.name}</RoomName>
              <GatewayInfo>
                {hasGateway ? (
                  <span style={{ color: '#4CAF50' }}>✓ {gwId}</span>
                ) : (
                  <span style={{ opacity: 0.5 }}>{gwId}</span>
                )}
              </GatewayInfo>
              {beaconsInRoom.length > 0 && (
                <BeaconsInRoom>
                  {beaconsInRoom.map(b => (
                    <BeaconDot key={b.id} color={b.color} title={b.label} />
                  ))}
                </BeaconsInRoom>
              )}
            </RoomCardContainer>
          );
        })}
      </RoomGrid>
    </RoomTypeSection>
  );
};

export const RoomPanel: React.FC<RoomPanelProps> = ({
  roomsByType,
  selectedBeacon,
  existingTestGateways,
  beaconLocations,
  dynamicBeacons,
  onRoomClick,
  onEnrollmentReaderClick,
}) => {
  // Get room types not in the standard order
  const otherRoomTypes = Object.keys(roomsByType).filter(type => !ROOM_TYPE_ORDER.includes(type));

  // Get all beacons (static + dynamic) at the enrollment reader
  const allBeacons: TestBeaconExtended[] = [
    ...TEST_BEACONS,
    ...dynamicBeacons.map(b => ({ ...b, type: 'patient' as const, entityName: b.label })),
  ];
  const beaconsAtEnrollmentReader = allBeacons.filter(b => beaconLocations[b.id] === ENROLLMENT_READER_GATEWAY_ID);
  const hasEnrollmentGateway = !!existingTestGateways[ENROLLMENT_READER_GATEWAY_ID];

  return (
    <Panel>
      <PanelTitle>
        Rooms {selectedBeacon && <span style={{ color: '#2196F3' }}>- Click to move beacon</span>}
      </PanelTitle>

      {/* Enrollment Reader */}
      <RoomTypeSection>
        <RoomTypeHeader>
          <Icon className="material-icons" style={{ fontSize: '1.2rem' }}>
            contactless
          </Icon>
          Enrollment Reader
        </RoomTypeHeader>
        <RoomGrid>
          <RoomCardContainer
            hasGateway={hasEnrollmentGateway}
            clickable={!!selectedBeacon}
            onClick={() => selectedBeacon && onEnrollmentReaderClick()}
          >
            <RoomName>Front Desk Scanner</RoomName>
            <GatewayInfo>
              {hasEnrollmentGateway ? (
                <span style={{ color: '#4CAF50' }}>✓ {ENROLLMENT_READER_GATEWAY_ID}</span>
              ) : (
                <span style={{ opacity: 0.5 }}>{ENROLLMENT_READER_GATEWAY_ID}</span>
              )}
            </GatewayInfo>
            {beaconsAtEnrollmentReader.length > 0 && (
              <BeaconsInRoom>
                {beaconsAtEnrollmentReader.map(b => (
                  <BeaconDot key={b.id} color={b.color} title={b.label} />
                ))}
              </BeaconsInRoom>
            )}
          </RoomCardContainer>
        </RoomGrid>
      </RoomTypeSection>

      {/* Standard room types in order */}
      {ROOM_TYPE_ORDER.map(roomType => (
        <RoomTypeSectionComponent
          key={roomType}
          roomType={roomType}
          rooms={roomsByType[roomType] || []}
          selectedBeacon={selectedBeacon}
          existingTestGateways={existingTestGateways}
          beaconLocations={beaconLocations}
          onRoomClick={onRoomClick}
        />
      ))}

      {/* Other room types */}
      {otherRoomTypes.map(roomType => (
        <RoomTypeSectionComponent
          key={roomType}
          roomType={roomType}
          rooms={roomsByType[roomType] || []}
          selectedBeacon={selectedBeacon}
          existingTestGateways={existingTestGateways}
          beaconLocations={beaconLocations}
          onRoomClick={onRoomClick}
        />
      ))}
    </Panel>
  );
};
